'use client';

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppLogo from '@/core/presentation/components/ui/AppLogo';
import { LogoLink } from '@/features/app/presentation/components/LogoLink';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#problema', label: 'O Problema' },
    { href: '#como-funciona', label: 'Como Funciona' },
    { href: '#beneficios', label: 'Benefícios' },
    { href: '#planos', label: 'Planos' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <>
      {/* Logo fixa: fora do nav, independente do header */}
      <LogoLink
        className="fixed left-0 md:left-14 z-[60] shrink-0 top-[calc(0.75rem+env(safe-area-inset-top,0px))]"
        ariaLabel="RetornaAr - Página inicial"
      >
        <AppLogo src="/assets/images/retornaArLogo.png" size={200} />
      </LogoLink>

      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-navy-dark/95 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-transparent'
        } pt-[calc(0.75rem+env(safe-area-inset-top,0px))] pb-3`}
      >
        <div className="max-w-[1320px] mx-auto px-6 flex items-center justify-between h-14">
          {/* Espaço à esquerda para a logo (que está fora do nav) */}
          <div className="w-28 shrink-0" aria-hidden />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-medium text-white/75 hover:text-white transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* CTA */}
        <Link
          to="/login"
          className="hidden md:inline-flex items-center gap-2 btn-primary text-sm font-bold px-5 py-2.5 rounded-full"
        >
          Entrar
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>

        {/* Mobile menu toggle */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden text-white p-2"
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {menuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu: altura máxima = viewport menos barra do nav (evita cortar o CTA como com max-h-80) */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
          menuOpen ?
            'max-h-[calc(100dvh-5.5rem-env(safe-area-inset-top,0px))] opacity-100'
          : 'max-h-0 opacity-0'
        }`}
      >
        <div className="max-h-[calc(100dvh-5.5rem-env(safe-area-inset-top,0px))] overflow-y-auto overscroll-contain bg-navy-dark/98 backdrop-blur-md px-6 py-4 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] flex flex-col gap-4 border-t border-white/10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="border-b border-white/10 py-2 font-medium text-white/80 last:border-0 hover:text-white"
            >
              {link.label}
            </a>
          ))}
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            className="btn-primary mt-1 shrink-0 rounded-full px-5 py-2.5 text-center text-sm font-bold sm:py-3 sm:text-base"
          >
            Entrar
          </Link>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Header;